import { sealEnvelopeText, openEnvelopeBytes } from "./aesgcm";
import { ensureSession, invalidateSession, reserveRequestCounter } from "./session";
import { utf8Decode, utf8Encode } from "./utils";

export type ResponseType = "json" | "text" | "blob" | "arrayBuffer" | "formData";

const SKIP_PREFIXES = [
  "/bettimax/handshake",
  "/bettimax/upload_file",
  "/bettimax/api/version",
  "/bettimax/sse/",
  "/bettimax/ws/",
];

export function shouldSkipCryptoPath(pathname: string): boolean {
  return SKIP_PREFIXES.some((p) => pathname.startsWith(p));
}

export function shouldUseCrypto(url: string, data: any, responseType: ResponseType): boolean {
  const u = new URL(url, window.location.origin);
  if (!u.pathname.startsWith("/bettimax/")) return false;
  if (shouldSkipCryptoPath(u.pathname)) return false;
  if (data instanceof FormData) return false;
  if (responseType !== "json" && responseType !== "text") return false;
  return true;
}

export async function buildCryptoRequest(input: {
  url: string;
  method: string;
  headers: Record<string, string>;
  data: any;
}): Promise<{
  url: string;
  headers: Record<string, string>;
  body?: BodyInit;
  sessionKey: Uint8Array;
}> {
  const u = new URL(input.url, window.location.origin);
  const apiOrigin = u.origin;

  if (input.data == null || input.method.toUpperCase() === "GET") {
    const s = await ensureSession(apiOrigin);
    return {
      url: input.url,
      headers: { ...input.headers, "X-Sid": s.sid },
      body: undefined,
      sessionKey: s.sessionKey,
    };
  }

  const { sid, sessionKey, counter } = await reserveRequestCounter(apiOrigin);
  const plaintext = JSON.stringify(input.data ?? {});
  const envelope = await sealEnvelopeText(sessionKey, counter, plaintext);
  return {
    url: input.url,
    headers: { ...input.headers, "X-Sid": sid, "Content-Type": "application/json" },
    body: envelope,
    sessionKey,
  };
}

export function tryParsePlainJSON(bytes: Uint8Array): any | null {
  try {
    const txt = utf8Decode(bytes);
    const j = JSON.parse(txt);
    if (typeof j === "object" && j !== null && "status_code" in j) return j;
    return null;
  } catch {
    return null;
  }
}

export async function decryptCryptoResponse(input: {
  sessionKey: Uint8Array;
  responseBytes: Uint8Array;
  responseType: ResponseType;
  contentType?: string;
}): Promise<
  | { kind: "ok"; data: any }
  | { kind: "plain_crypto_error"; status_code: number; error_code?: string; data?: any }
> {
  if (input.contentType && input.contentType.includes("text/html")) {
    return { kind: "ok", data: utf8Decode(input.responseBytes) };
  }

  const plain = tryParsePlainJSON(input.responseBytes);
  if (plain && typeof plain.status_code === "number") {
    return {
      kind: "plain_crypto_error",
      status_code: plain.status_code,
      error_code: plain.error_code,
      data: plain.data,
    };
  }

  const opened = await openEnvelopeBytes(input.sessionKey, input.responseBytes);
  const text = utf8Decode(opened.plaintext);
  if (input.responseType === "text") return { kind: "ok", data: text };
  return { kind: "ok", data: JSON.parse(text) };
}

export function shouldRetryCryptoStatus(statusCode: number): boolean {
  return statusCode === 4001 || statusCode === 4002 || statusCode === 4003 || statusCode === 4004;
}

export function resetCryptoSession() {
  invalidateSession();
}

export function encodeBodyForNonCrypto(data: any): BodyInit | undefined {
  if (data == null) return undefined;
  if (data instanceof FormData) return data;
  if (typeof data === "string") return data;
  if (data instanceof Uint8Array) return data;
  return JSON.stringify(data);
}

export function decodeNonCryptoResponse(input: { responseType: ResponseType; bytes: Uint8Array }): any {
  if (input.responseType === "arrayBuffer") return input.bytes.buffer.slice(input.bytes.byteOffset, input.bytes.byteOffset + input.bytes.byteLength);
  if (input.responseType === "text") return utf8Decode(input.bytes);
  if (input.responseType === "json") return JSON.parse(utf8Decode(input.bytes));
  return input.bytes;
}

export function toUint8Array(buf: ArrayBuffer): Uint8Array {
  return new Uint8Array(buf);
}

export function toBytesFromText(text: string): Uint8Array {
  return utf8Encode(text);
}
